/* eslint-disable import/prefer-default-export */
import { eachDayOfInterval, format } from 'date-fns';

import { postSummaryPending } from '../../services/workReport';

/**
 * @typedef FirstColumn
 * @property {string} agrupadoPor
 * @property {boolean} finaliza
 * @property {boolean} isDisabled
 * @property {DailyReport["idSolicitud"]} idSolicitud
 * @property {DailyReport["tipoAsignacion"]} tipoAsignacion
 * @property {string} titulo
 */

/**
 * @typedef SecondColumn
 * @property {string} agrupadoPor
 * @property {boolean} cambioImputacion
 * @property {boolean} editaImputacion
 * @property {string?} equipo
 * @property {number} idParteLinea
 * @property {number} idPartetrabajo
 * @property {number?} idPartetrabajoUbicacionLinea
 * @property {number} idProveedor
 * @property {boolean} isDisabled
 * @property {string} lineaSap
 * @property {string} lineaYClic
 * @property {string} nroSolicitud
 * @property {string} numeroLinea
 * @property {string} numeroPosicion
 * @property {string} posicionSap
 * @property {string} posicionYClic
 * @property {boolean} showHeader
 * @property {string} titulo
 * @property {string} ubicacion
 * @property {string} unidadMedida
 */

/**
 * @typedef DailyReport
 * @property {number?} cantidad
 * @property {boolean} certificaYclick
 * @property {string} colorInspeccion
 * @property {string} coloresEstado
 * @property {number?} estadoCertificacionEnum
 * @property {number} estadoEnum
 * @property {string} fecha
 * @property {boolean} habilitaCheck
 * @property {number} idParteDiario
 * @property {number} idSolicitud
 * @property {string} nroSolicitud
 * @property {boolean} rechazaCantidad
 * @property {number} tipoAsignacion
 */

/**
 * @typedef PositionLine
 * @property {FirstColumn?} firstColumn
 * @property {SecondColumn} secondColumn
 * @property {(DailyReport | string)[]} weeklyReport
 */

/** @typedef {{key: string, group: PositionLine[]}[]} TableData */

/**
 * @typedef TableDataPagination
 * @property {number} pageIndex
 * @property {number} totalCount
 * @property {number} totalPages
 */

/** @typedef {{ pagination: TableDataPagination, tableData: TableData}} WeeklyWorkReport */

/** @param {{startDay: string, endDay: string, dailyReport: [], nroSolicitud: string}} @returns {(DailyReport| string)[]} */
const buildWeeklyWorkPart = ({ startDay, endDay, dailyReport, nroSolicitud }) => {
  const daysInterval = eachDayOfInterval({
    start: new Date(startDay),
    end: new Date(endDay)
  });

  const daysMap = new Map(
    dailyReport.map((day) => [format(new Date(day.fecha), 'yyyy-MM-dd'), day])
  );

  const formattedWeek = [];

  daysInterval.forEach((day) => {
    const dayInMap = daysMap.get(format(day, 'yyyy-MM-dd'));

    if (!dayInMap) {
      formattedWeek.push(format(day, 'yyyy-MM-dd'));
    } else {
      formattedWeek.push({
        cantidad: dayInMap.cantidad,
        certificaYclick: dayInMap.certificaYclick,
        colorInspeccion: dayInMap.colorInspeccion,
        coloresEstado: dayInMap.coloresEstado,
        estadoCertificacionEnum: dayInMap.estadoCertificacionEnum,
        estadoEnum: dayInMap.estadoEnum,
        fecha: dayInMap.fecha,
        habilitaCheck: dayInMap.habilitaCheck,
        idParteDiario: dayInMap.idParteDiario,
        idSolicitud: dayInMap.idSolicitud,
        nroSolicitud,
        rechazaCantidad: dayInMap.rechazaCantidad,
        tipoAsignacion: dayInMap.tipoAsignacion,
        tipoUbicacionEnum: dayInMap.tipoUbicacionEnum
      });
    }
  });

  return formattedWeek;
};

/** @param {unknown} data @returns {WeeklyWorkReport} */
export const getWeeklyReportAdapter = (data) => {
  // This helper indicates when a new group starts
  // during iteration and adds the first column (Line 198).
  let aux = -1;

  const pagination = {
    pageIndex: data.pageIndex,
    totalCount: data.totalCount,
    totalPages: data.totalPages
  };

  if (!data.items) return { pagination, tableData: [] };

  const tableData = data.items?.reduce((arr, currentGroup, currentGroupIndex) => {
    const group = [];

    const key = `${currentGroup.agrupadoPor}-${currentGroup.titulo}`;

    const isDisabledEachPositionLine = currentGroup.items.map(({ posicionesLineas }) =>
      posicionesLineas.map(({ partesTrabajoDiario }) =>
        partesTrabajoDiario.every(({ habilitaCheck }) => !habilitaCheck)
      )
    );

    currentGroup.items.forEach(
      (
        {
          agrupadoPor,
          titulo,
          posicionesLineas,
          idUbicacionTecnica,
          objetoImputacion,
          objetoImputacionModificado,
          solicitudMensualizada,
          idUbicacion,
          contieneArchivosAdjuntos
        },
        lineIndex
      ) => {
        const isGroupDisabled = isDisabledEachPositionLine.flat().every((disabled) => disabled);

        posicionesLineas.forEach(({ partesTrabajoDiario, ...restPositionLine }, index) => {
          const isDisabledPositionLine = isDisabledEachPositionLine[lineIndex][index];
          const secondColumn = {
            agrupadoPor,
            primerAgrupador: currentGroup.agrupadoPor,
            cambioImputacion: restPositionLine.cambioImputacion,
            editaImputacion: restPositionLine.editaImputacion,
            equipo: restPositionLine.equipo,
            eventoSigla: restPositionLine.eventoSigla,
            idUbicacion,
            idParteLinea: restPositionLine.idParteLinea,
            idPartetrabajo: restPositionLine.idPartetrabajo,
            idPartetrabajoUbicacionLinea: restPositionLine.idPartetrabajoUbicacionLinea,
            idProveedor: restPositionLine.idProveedor,
            isDisabled: isDisabledPositionLine,
            lineaSap: restPositionLine.lineaSap,
            lineaYClic: restPositionLine.lineaYClic,
            nroSolicitud: restPositionLine.nroSolicitud,
            numeroLinea: restPositionLine.numeroLinea,
            numeroPosicion: restPositionLine.numeroPosicion,
            posicionSap: restPositionLine.posicionSap,
            posicionYClic: restPositionLine.posicionYClic,
            showHeader: index === 0,
            titulo,
            ubicacion: restPositionLine.ubicacion,
            unidadMedida: restPositionLine.unidadMedida,
            tieneUbicacionesTecnicas: Boolean(idUbicacionTecnica),
            objetoImputacion,
            objetoImputacionModificado,
            solicitudMensualizada,
            contieneArchivosAdjuntos,
            idSolicitud: partesTrabajoDiario[0]?.idSolicitud || null
          };
          const weeklyReport = buildWeeklyWorkPart({
            dailyReport: partesTrabajoDiario,
            endDay: data.ultimaFecha,
            nroSolicitud: restPositionLine.nroSolicitud,
            startDay: data.primerFecha
          });

          const dailyReport = weeklyReport.find((workPart) => workPart.idParteDiario);

          if (aux !== currentGroupIndex) {
            aux += 1;
            group.push({
              firstColumn: {
                agrupadoPor: currentGroup.agrupadoPor,
                finaliza: currentGroup.finaliza,
                idSolicitud: dailyReport?.idSolicitud,
                isDisabled: isGroupDisabled,
                tipoAsignacion: dailyReport?.tipoAsignacion,
                titulo: currentGroup.titulo
              },
              secondColumn,
              weeklyReport
            });
          } else {
            group.push({ secondColumn, weeklyReport });
          }
        });
      }
    );

    arr.push({ key, group });

    return arr;
  }, []);

  return { pagination, tableData };
};

export const postSummaryPendingAdapter = (filters) =>
  new Promise((resolve, reject) => {
    postSummaryPending(filters)
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });
